// src/components/common/layout/Navbar.js
// Updated: 06/20/2026

import React, { useState } from 'react'; 
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useFamily } from '../../../context/FamilyContext';
import { auth } from '../../../firebase/config';
import { signOut } from 'firebase/auth';
import toast from 'react-hot-toast';
import { FaSignOutAlt, FaUserCircle, FaHome, FaUsers, FaTree, FaAddressBook, FaImages, FaBars, FaTimes, FaComments } from 'react-icons/fa'; // Added FaComments

const Navbar = () => {
  const { userData } = useAuth();
  const { familyData, isHost } = useFamily();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false); 

  const handleLogout = async () => {
    try {
      await signOut(auth);
      toast.success('Signed out successfully');
      navigate('/login');
    } catch (error) {
      toast.error('Failed to sign out');
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  }

  // Define nav links data to avoid repetition
  // Added the new Feed/Message Board link
  const navLinks = [
    { to: "/", icon: FaHome, text: "Home" },
    ...(isHost ? [{ to: "/dashboard", icon: FaUsers, text: "Dashboard" }] : []),
    { to: "/feed", icon: FaComments, text: "Message Board" },
    { to: "/directory", icon: FaAddressBook, text: "Directory" },
    { to: "/gallery", icon: FaImages, text: "Gallery" },
    { to: "/family-tree", icon: FaTree, text: "Family Tree" },
  ];

  return (
    <nav className="bg-white shadow-md relative"> 
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left side: Hamburger Menu Button (visible on small screens) */}
          <div className="md:hidden">
            <button
              onClick={toggleMobileMenu}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-neutral-gray hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
              aria-controls="mobile-menu"
              aria-expanded={isMobileMenuOpen}
            >
              <span className="sr-only">Open main menu</span>
              {isMobileMenuOpen ? (
                <FaTimes className="block h-6 w-6" aria-hidden="true" />
              ) : (
                <FaBars className="block h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>

          {/* Center: Brand/Family Name */}
          <div className="flex-shrink-0 flex items-center">
            <Link to="/" className="text-2xl font-bold text-primary">
              {familyData?.familyName || 'Family Reunion'}
            </Link>
          </div>

          {/* Center: Desktop Navigation Links (hidden on small screens) */}
          <div className="hidden md:block md:ml-6">
            <div className="flex items-baseline space-x-4">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="text-gray-600 hover:bg-gray-100 hover:text-neutral-gray px-3 py-2 rounded-md text-sm font-medium flex items-center gap-2 transition-colors duration-150"
                >
                  <link.icon /> {link.text}
                </Link>
              ))}
            </div>
          </div>

          {/* Right side: User Info & Logout (always visible) */}
          <div className="flex items-center ml-4 md:ml-6">
            <span className="text-gray-700 text-sm mr-3 hidden lg:block">
              Welcome, {userData?.firstName || 'User'}
            </span>
            <Link to="/profile" className="p-1 rounded-full text-gray-400 hover:text-neutral-gray focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary" title="Profile">
              <span className="sr-only">View profile</span>
              <FaUserCircle className="h-6 w-6" />
            </Link>
            <button
              onClick={handleLogout}
              className="ml-3 p-1 rounded-full text-gray-400 hover:text-neutral-gray focus:outline-none"
              title="Sign Out"
            >
              <span className="sr-only">Sign out</span>
              <FaSignOutAlt className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu (conditionally rendered) */}
      {isMobileMenuOpen && (
        <div className="md:hidden absolute top-16 left-0 w-full bg-white shadow-lg z-50 border-t border-gray-200" id="mobile-menu">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={closeMobileMenu} // Close menu on link click
                className="text-gray-600 hover:bg-gray-100 hover:text-neutral-gray block px-3 py-2 rounded-md text-base font-medium flex items-center gap-2"
              >
                <link.icon className="text-lg" /> {link.text}
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;